import { memo, useCallback, useEffect, useMemo, useRef, useState, useTransition, type MouseEvent, type ReactNode, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { toast } from "sonner";
import {
  Archive,
  ArchiveRestore,
  Building2,
  Check,
  ChevronDown,
  Clock,
  Copy,
  Download,
  Globe2,
  Loader2,
  Lock,
  LockOpen,
  LogIn,
  MailPlus,
  Mail,
  XCircle,
  Pencil,
  RefreshCw,
  Repeat,
  RotateCcw,
  Search,
  Settings2,
  Shield,
  Trash2,
  UserCheck,
  UserCog,
  UserMinus,
  UserPlus,
  UserRound,
  UserX,
  X,
  Zap,
} from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import { AUDIT_CATEGORY_ACTIONS } from "@nexus/contracts";
import {
  globalAuditLogInfiniteQueryOptions,
  globalAuditLogQueryOptions,
} from "@/features/identity-hub/lib/queries/audit-log.queries";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import type { AuditLogItem } from "@/features/identity-hub/api/audit-log.api";
import { relativeTimeAudit } from "@/features/identity-hub/utils/time";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";

// ─── Types ────────────────────────────────────────────────────────────────────

type AuditAction =
  | "role_assigned"
  | "role_revoked"
  | "role_replaced"
  | "user_invited"
  | "user_activated"
  | "user_updated"
  | "account_suspended"
  | "account_reactivated"
  | "org_created"
  | "org_updated"
  | "org_deactivated"
  | "org_reactivated"
  | "perm_changed"
  | "role_created"
  | "role_updated"
  | "role_deleted"
  | "role_restored"
  | "invite_created"
  | "invite_regenerated"
  | "invite_resent"
  | "invite_revoked"
  | "login"
  | "mfa_enabled"
  | "mfa_disabled";

type ActionCategory = "all" | "access" | "identity" | "orgs" | "auth";
type DateRange = "today" | "7d" | "30d" | "all";

interface AuditActor {
  id: string;
  name: string;
  email: string;
}

interface AuditEntry {
  id: string;
  timestamp: string;
  /** Null when the system couldn't attribute the event to a real user. */
  actor: AuditActor | null;
  action: AuditAction;
  /** Email is null for non-user targets (roles, orgs) and unresolved snapshots. */
  target: { type: "user" | "role" | "org" | "invite"; id: string; name: string; email: string | null };
  /** Set only when a resolved org name is available — used for description text. */
  org?: { id: string; name: string };
  /** Raw tenant ID — always set when known, used as fallback in the detail panel. */
  tenantId?: string;
  meta?: {
    from?: string;
    to?: string;
    /** For role_replaced — the role being swapped out. */
    fromRole?: string;
    /** For role_replaced — the role being swapped in. */
    toRole?: string;
    ip?: string;
    device?: string;
    location?: string;
    requestId?: string;
    userAgent?: string;
    /** Human-readable list of changed field keys (for *_updated events). */
    changedFields?: string[];
    /** Name change tracking — present when a name/displayName field changed. */
    nameBefore?: string;
    nameAfter?: string;
    /** Permission diff summary (for perm_changed). */
    addedPermsCount?: number;
    removedPermsCount?: number;
  };
}

// ─── API → UI adapter ─────────────────────────────────────────────────────────

function targetTypeToUi(t: string): "user" | "role" | "org" | "invite" {
  if (t === "ACCESS") return "role";
  if (t === "TENANT") return "org";
  if (t === "INVITE") return "invite";
  return "user";
}

function apiItemToEntry(item: AuditLogItem): AuditEntry {
  const meta = item.metadata as Record<string, unknown>;
  const accessName = typeof meta["accessName"] === "string" ? meta["accessName"] : undefined;
  const tenantName = typeof meta["tenantName"] === "string" ? meta["tenantName"] : undefined;
  const isGrant  = item.action === "role_assigned";
  const isRevoke = item.action === "role_revoked";
  // Invite events store the role-being-granted in `accessName` — promote it to
  // `meta.to` so the same describe-template slot works across grants and invites.
  const isInvite = item.action === "invite_created";

  // Role-swap: backend snapshots both names so audit reads "Editor → Admin".
  const fromAccessName = typeof meta["fromAccessName"] === "string" ? meta["fromAccessName"] : undefined;
  const toAccessName   = typeof meta["toAccessName"]   === "string" ? meta["toAccessName"]   : undefined;

  // Generic "what fields changed?" extractor used by *_updated events.
  const changes = (meta["changes"] && typeof meta["changes"] === "object")
    ? (meta["changes"] as Record<string, unknown>)
    : undefined;
  const changedFields = changes ? Object.keys(changes) : undefined;

  const nameBefore = typeof meta["nameBefore"] === "string" ? meta["nameBefore"] : undefined;
  const nameAfter  = typeof meta["nameAfter"]  === "string" ? meta["nameAfter"]  : undefined;

  // Permission diff summary (perm_changed). The handler stores the new full
  // permission list under metadata.permissions — we surface counts so the
  // audit row reads "added 3 / removed 1" without dumping the whole array.
  const permsArr = Array.isArray(meta["permissions"]) ? meta["permissions"] : undefined;
  const addedPermsCount   = typeof meta["addedPermsCount"]   === "number" ? (meta["addedPermsCount"]   as number) : undefined;
  const removedPermsCount = typeof meta["removedPermsCount"] === "number" ? (meta["removedPermsCount"] as number) : undefined;

  const hasKnownActor = !!(item.actorDisplayName ?? item.actorEmail);

  return {
    id:        item.id,
    timestamp: item.createdAt,
    actor: hasKnownActor
      ? {
          id:    item.actorUserId ?? "unknown",
          name:  item.actorDisplayName ?? item.actorEmail ?? "",
          email: item.actorEmail ?? "",
        }
      : null,
    action:  item.action as AuditAction,
    target: {
      type:  targetTypeToUi(item.targetType),
      id:    item.targetId,
      name:  item.targetName ?? item.targetId,
      email: item.targetEmail,
    },
    org: tenantName ? { id: item.tenantId ?? "", name: tenantName } : undefined,
    tenantId: item.tenantId ?? undefined,
    meta: {
      ip:        item.ip ?? undefined,
      requestId: item.correlationId ?? undefined,
      userAgent: item.userAgent ?? undefined,
      to:        typeof meta["to"] === "string" ? meta["to"] : (isGrant || isInvite) ? accessName : undefined,
      from:      typeof meta["from"] === "string" ? meta["from"] : isRevoke ? accessName : undefined,
      fromRole:  fromAccessName,
      toRole:    toAccessName,
      device:    typeof meta["device"] === "string" ? meta["device"] : parseUserAgent(item.userAgent),
      location:  typeof meta["location"] === "string" ? meta["location"] : undefined,
      changedFields,
      nameBefore,
      nameAfter,
      addedPermsCount:   addedPermsCount   ?? (permsArr ? permsArr.length : undefined),
      removedPermsCount: removedPermsCount,
    },
  };
}

// Lightweight UA parser — extracts a friendly "Browser on OS" label.
// Avoids an extra dependency for what is purely a presentational hint.
function parseUserAgent(ua: string | null | undefined): string | undefined {
  if (!ua) return undefined;
  let browser = "Browser";
  if (/Edg\//.test(ua))            browser = "Edge";
  else if (/Firefox\//.test(ua))   browser = "Firefox";
  else if (/Chrome\//.test(ua))    browser = "Chrome";
  else if (/Safari\//.test(ua))    browser = "Safari";

  let os = "";
  if (/Windows NT/.test(ua))                  os = "Windows";
  else if (/Mac OS X|Macintosh/.test(ua))     os = "macOS";
  else if (/Android/.test(ua))                os = "Android";
  else if (/iPhone|iPad|iPod/.test(ua))       os = "iOS";
  else if (/Linux/.test(ua))                  os = "Linux";

  return os ? `${browser} on ${os}` : browser;
}

// ─── Action config ────────────────────────────────────────────────────────────

interface ActionCfg {
  Icon: ComponentType<{ className?: string }>;
  iconColor: string;
  iconBg: string;
  labelKey: string;
  describeKey: string;
  describeVars: (e: AuditEntry, t: TFunction) => Record<string, string>;
}

// Builders for the optional `(email)`, `in **Org**`, `(name)`, etc. clauses.
// Centralising them keeps every describe template consistent and avoids
// stray parens/whitespace when a field is missing.
const targetEmailSuffix = (e: AuditEntry) => (e.target.email ? ` (${e.target.email})` : "");
const orgSuffix         = (e: AuditEntry, t: TFunction) =>
  e.org?.name ? ` ${t("iam.auditLog.inOrg")} **${e.org.name}**` : "";

const targetName = (e: AuditEntry, t: TFunction) =>
  e.target.name || t("iam.auditLog.fallback.aUser");
const orgName    = (e: AuditEntry, t: TFunction) =>
  e.org?.name   || t("iam.auditLog.fallback.anOrg");

/**
 * Maps a list of changed field keys to a friendly, parenthesised summary.
 *
 * When a name/displayName field changed and before/after values are available,
 * renders "renamed from **X** to **Y**" instead of the generic "(name)" suffix.
 * Other changed fields are appended as "(and N more)" if present.
 *
 * Falls back gracefully on unknown keys by camelCase-splitting them.
 */
function changesSuffix(e: AuditEntry, t: TFunction): string {
  const fields = e.meta?.changedFields ?? [];
  if (fields.length === 0) return "";

  const nameField = fields.find((f) => f === "name" || f === "displayName");
  const otherFields = fields.filter((f) => f !== "name" && f !== "displayName");
  const nameBefore = e.meta?.nameBefore;
  const nameAfter  = e.meta?.nameAfter;
  // Guard against a no-op rename: callers occasionally include the unchanged
  // name alongside other edits. Treat equal before/after as "name didn't
  // change" so we don't render "renamed from X to X".
  const nameChanged = nameField !== undefined
    && nameBefore !== undefined
    && nameAfter !== undefined
    && nameBefore !== nameAfter;

  if (nameChanged) {
    const renamePart = ` ${t("iam.auditLog.changes.renamedFrom", {
      from: nameBefore,
      to:   nameAfter,
      defaultValue: "from **{{from}}** to **{{to}}**",
    })}`;
    if (otherFields.length === 0) return renamePart;
    return `${renamePart} ${t("iam.auditLog.changes.andMore", { count: otherFields.length, defaultValue: "and {{count}} more" })}`;
  }

  // If name appeared in changedFields but didn't actually change, drop it so
  // the suffix only mentions the fields that genuinely changed.
  const reportable = nameField && nameBefore === nameAfter ? otherFields : fields;
  if (reportable.length === 0) return "";

  const friendly = reportable.map((f) => {
    const translated = t(`iam.auditLog.changes.fields.${f}`, { defaultValue: "" });
    if (translated) return translated;
    // Decamel "industryIndices" → "industry indices"
    return f.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  });

  const [first, ...rest] = friendly;
  if (rest.length === 0) return ` (${first})`;
  return ` (${first} ${t("iam.auditLog.changes.andMore", { count: rest.length, defaultValue: "and {{count}} more" })})`;
}

/**
 * Compact summary for permission changes: "+3 / -1" when both sides change,
 * "+3 permissions" or "-1 permission" when only one side did. We compute it
 * from added/removedPermsCount when available; otherwise omit entirely so the
 * sentence reads naturally without the suffix.
 */
function permsSummarySuffix(e: AuditEntry): string {
  const added   = e.meta?.addedPermsCount   ?? 0;
  const removed = e.meta?.removedPermsCount ?? 0;
  if (added === 0 && removed === 0) return "";
  if (added > 0 && removed > 0)     return ` (+${added} / −${removed})`;
  if (added > 0)                    return ` (+${added})`;
  return ` (−${removed})`;
}

// Fallback rendered when the backend emits a brand-new action the frontend
// hasn't taught itself about yet. Prevents the row from crashing while still
// surfacing the raw event name so the gap is visible in QA.
const UNKNOWN_ACTION_CFG: ActionCfg = {
  Icon: Zap,
  iconColor: "text-muted-foreground",
  iconBg: "bg-muted",
  labelKey: "iam.auditLog.actions.unknown",
  describeKey: "iam.auditLog.describe.unknown",
  describeVars: (e) => ({ action: e.action }),
};

export const ACTION_CONFIG: Record<AuditAction, ActionCfg> = {
  role_assigned:      { Icon: UserPlus,  iconColor: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-500/10", labelKey: "iam.auditLog.actions.role_assigned",      describeKey: "iam.auditLog.describe.role_assigned",      describeVars: (e, t) => ({ role: e.meta?.to   || t("iam.auditLog.fallback.aRole"), target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e), orgSuffix: orgSuffix(e, t) }) },
  role_revoked:       { Icon: UserMinus, iconColor: "text-destructive",                       iconBg: "bg-destructive/10", labelKey: "iam.auditLog.actions.role_revoked",       describeKey: "iam.auditLog.describe.role_revoked",       describeVars: (e, t) => ({ role: e.meta?.from || t("iam.auditLog.fallback.aRole"), target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e), orgSuffix: orgSuffix(e, t) }) },
  role_replaced:      { Icon: Repeat,    iconColor: "text-blue-500",                          iconBg: "bg-blue-500/10",    labelKey: "iam.auditLog.actions.role_replaced",      describeKey: "iam.auditLog.describe.role_replaced",      describeVars: (e, t) => ({ fromRole: e.meta?.fromRole || t("iam.auditLog.fallback.aRole"), toRole: e.meta?.toRole || t("iam.auditLog.fallback.aRole"), target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e), orgSuffix: orgSuffix(e, t) }) },
  user_invited:       { Icon: MailPlus,  iconColor: "text-blue-500",                          iconBg: "bg-blue-500/10",    labelKey: "iam.auditLog.actions.user_invited",       describeKey: "iam.auditLog.describe.user_invited",       describeVars: (e, t) => ({ target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e), orgSuffix: orgSuffix(e, t) }) },
  user_activated:     { Icon: UserCheck, iconColor: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-500/10", labelKey: "iam.auditLog.actions.user_activated",     describeKey: "iam.auditLog.describe.user_activated",     describeVars: (e, t) => ({ orgSuffix: orgSuffix(e, t) }) },
  user_updated:       { Icon: UserCog,   iconColor: "text-amber-500",                         iconBg: "bg-amber-500/10",   labelKey: "iam.auditLog.actions.user_updated",       describeKey: "iam.auditLog.describe.user_updated",       describeVars: (e, t) => ({ target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e), changesSuffix: changesSuffix(e, t) }) },
  account_suspended:   { Icon: UserX,     iconColor: "text-destructive",                       iconBg: "bg-destructive/10", labelKey: "iam.auditLog.actions.account_suspended",   describeKey: "iam.auditLog.describe.account_suspended",   describeVars: (e, t) => ({ target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e) }) },
  account_reactivated: { Icon: UserCheck, iconColor: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-500/10", labelKey: "iam.auditLog.actions.account_reactivated", describeKey: "iam.auditLog.describe.account_reactivated", describeVars: (e, t) => ({ target: targetName(e, t), targetEmailSuffix: targetEmailSuffix(e) }) },
  org_created:        { Icon: Building2, iconColor: "text-violet-500",                        iconBg: "bg-violet-500/10",  labelKey: "iam.auditLog.actions.org_created",        describeKey: "iam.auditLog.describe.org_created",        describeVars: (e, t) => ({ target: targetName(e, t) }) },
  org_updated:        { Icon: Pencil,    iconColor: "text-amber-500",                         iconBg: "bg-amber-500/10",   labelKey: "iam.auditLog.actions.org_updated",        describeKey: "iam.auditLog.describe.org_updated",        describeVars: (e, t) => ({ target: targetName(e, t), changesSuffix: changesSuffix(e, t) }) },
  org_deactivated:    { Icon: Archive,        iconColor: "text-destructive",                       iconBg: "bg-destructive/10", labelKey: "iam.auditLog.actions.org_deactivated",    describeKey: "iam.auditLog.describe.org_deactivated",    describeVars: (e, t) => ({ target: targetName(e, t) }) },
  org_reactivated:    { Icon: ArchiveRestore, iconColor: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-500/10", labelKey: "iam.auditLog.actions.org_reactivated",    describeKey: "iam.auditLog.describe.org_reactivated",    describeVars: (e, t) => ({ target: targetName(e, t) }) },
  perm_changed:       { Icon: Settings2,      iconColor: "text-amber-500",                         iconBg: "bg-amber-500/10",   labelKey: "iam.auditLog.actions.perm_changed",       describeKey: "iam.auditLog.describe.perm_changed",       describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t), permsSummarySuffix: permsSummarySuffix(e) }) },
  role_created:       { Icon: Shield,         iconColor: "text-violet-500",                        iconBg: "bg-violet-500/10",  labelKey: "iam.auditLog.actions.role_created",       describeKey: "iam.auditLog.describe.role_created",       describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t) }) },
  role_updated:       { Icon: Pencil,         iconColor: "text-amber-500",                         iconBg: "bg-amber-500/10",   labelKey: "iam.auditLog.actions.role_updated",       describeKey: "iam.auditLog.describe.role_updated",       describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t), changesSuffix: changesSuffix(e, t) }) },
  role_deleted:       { Icon: Trash2,         iconColor: "text-destructive",                       iconBg: "bg-destructive/10", labelKey: "iam.auditLog.actions.role_deleted",       describeKey: "iam.auditLog.describe.role_deleted",       describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t) }) },
  role_restored:      { Icon: RotateCcw,      iconColor: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-500/10", labelKey: "iam.auditLog.actions.role_restored",      describeKey: "iam.auditLog.describe.role_restored",      describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t) }) },
  invite_created:     { Icon: MailPlus,  iconColor: "text-blue-500",                          iconBg: "bg-blue-500/10",    labelKey: "iam.auditLog.actions.invite_created",     describeKey: "iam.auditLog.describe.invite_created",     describeVars: (e, t) => ({ target: targetName(e, t), role: e.meta?.to || t("iam.auditLog.fallback.aRole"), orgSuffix: orgSuffix(e, t) }) },
  invite_regenerated: { Icon: RefreshCw, iconColor: "text-amber-500",                         iconBg: "bg-amber-500/10",   labelKey: "iam.auditLog.actions.invite_regenerated", describeKey: "iam.auditLog.describe.invite_regenerated", describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t) }) },
  invite_resent:      { Icon: Mail,      iconColor: "text-blue-500",                          iconBg: "bg-blue-500/10",    labelKey: "iam.auditLog.actions.invite_resent",      describeKey: "iam.auditLog.describe.invite_resent",      describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t) }) },
  invite_revoked:     { Icon: XCircle,   iconColor: "text-destructive",                       iconBg: "bg-destructive/10", labelKey: "iam.auditLog.actions.invite_revoked",     describeKey: "iam.auditLog.describe.invite_revoked",     describeVars: (e, t) => ({ target: targetName(e, t), orgSuffix: orgSuffix(e, t) }) },
  login:              { Icon: LogIn,     iconColor: "text-muted-foreground",                  iconBg: "bg-muted",          labelKey: "iam.auditLog.actions.login",              describeKey: "iam.auditLog.describe.login",              describeVars: () => ({}) },
  mfa_enabled:        { Icon: Lock,      iconColor: "text-emerald-600 dark:text-emerald-400", iconBg: "bg-emerald-500/10", labelKey: "iam.auditLog.actions.mfa_enabled",        describeKey: "iam.auditLog.describe.mfa_enabled",        describeVars: (e, t) => ({ target: targetName(e, t) }) },
  mfa_disabled:       { Icon: LockOpen,  iconColor: "text-destructive",                       iconBg: "bg-destructive/10", labelKey: "iam.auditLog.actions.mfa_disabled",       describeKey: "iam.auditLog.describe.mfa_disabled",       describeVars: (e, t) => ({ target: targetName(e, t) }) },
};

const ACTION_CATEGORIES: ActionCategory[] = ["all", "access", "identity", "orgs", "auth"];
const DATE_RANGES: DateRange[] = ["today", "7d", "30d", "all"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dateKey(isoString: string, t: TFunction): string {
  const ts = new Date(isoString);
  const diffDays = Math.floor((Date.now() - ts.getTime()) / 86400000);
  if (diffDays === 0) return t("iam.auditLog.dateGroup.today");
  if (diffDays === 1) return t("iam.auditLog.dateGroup.yesterday");
  return ts.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function inRange(isoString: string, range: DateRange): boolean {
  if (range === "all") return true;
  const diffDays = (Date.now() - new Date(isoString).getTime()) / 86400000;
  if (range === "today") return diffDays < 1;
  if (range === "7d")    return diffDays < 7;
  return diffDays < 30;
}

function parseBold(text: string): ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : part,
  );
}

function dateRangeToFromDate(range: DateRange): string | undefined {
  if (range === "all") return undefined;
  const msMap: Record<string, number> = { today: 86_400_000, "7d": 604_800_000, "30d": 2_592_000_000 };
  return new Date(Date.now() - msMap[range]!).toISOString();
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-all",
        active
          ? "border-transparent bg-primary text-primary-foreground shadow-sm"
          : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function CopyButton({
  value,
  label,
  t,
  size = "sm",
}: {
  value: string;
  label: string;
  t: TFunction;
  size?: "sm" | "xs";
}) {
  const { copied, copy } = useCopyToClipboard({
    resetMs: 1400,
    onCopy: () => toast.success(t("iam.auditLog.detail.copiedToast")),
  });

  const handleClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    await copy(value);
  };

  const dim = size === "xs" ? "h-4 w-4" : "h-5 w-5";
  const iconDim = size === "xs" ? "h-2.5 w-2.5" : "h-3 w-3";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={handleClick}
          aria-label={`${t("iam.auditLog.detail.copy")} ${label}`}
          className={cn(
            "inline-flex shrink-0 items-center justify-center rounded text-muted-foreground/50 transition",
            dim,
            "opacity-40 group-hover/detail-row:opacity-100 group-hover/event-panel:opacity-70 focus-visible:opacity-100",
            "hover:bg-background hover:text-foreground hover:shadow-sm hover:opacity-100",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            copied && "opacity-100 text-emerald-600 dark:text-emerald-400",
          )}
        >
          {copied ? <Check className={iconDim} /> : <Copy className={iconDim} />}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {copied ? t("iam.auditLog.detail.copied") : t("iam.auditLog.detail.copy")}
      </TooltipContent>
    </Tooltip>
  );
}

// Tailwind 4 arbitrary values use underscores, not commas, between tracks.
function DetailRow({
  label,
  value,
  copyValue,
  mono = false,
  t,
}: {
  label: string;
  value: ReactNode;
  copyValue?: string;
  mono?: boolean;
  t: TFunction;
}) {
  return (
    <div className="group/detail-row grid grid-cols-[88px_1fr_16px] items-center gap-3 py-[3px]">
      <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground/70">{label}</span>
      <span className={cn("min-w-0 truncate text-[12px] leading-tight text-foreground", mono && "font-mono text-[11.5px]")}>
        {value}
      </span>
      {copyValue ? <CopyButton value={copyValue} label={label} t={t} size="xs" /> : <span aria-hidden />}
    </div>
  );
}

function DetailSection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col">
      <div className="mb-1 flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground/60" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{title}</span>
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

/**
 * Memoized row — only re-renders when its own `isExpanded`/`isHighlighted`
 * flags change. Callbacks use stable identities (passed via useCallback from
 * the parent) so React.memo can actually skip unchanged rows.
 *
 * Props accept `onToggle(id)` and `onSetRef(id, el)` so the parent never
 * needs to create a per-row closure.
 */
const AuditEntryRow = memo(function AuditEntryRow({
  entry,
  isLast,
  isExpanded,
  isHighlighted,
  onToggle,
  onSetRef,
  t,
}: {
  entry: AuditEntry;
  isLast: boolean;
  isExpanded: boolean;
  isHighlighted: boolean;
  /** Stable callback — parent passes useCallback'd toggleExpand(id). */
  onToggle: (id: string) => void;
  /** Stable callback — parent passes useCallback'd setRowRef(id, el). */
  onSetRef: (id: string, el: HTMLDivElement | null) => void;
  t: TFunction;
}) {
  const cfg = ACTION_CONFIG[entry.action] ?? UNKNOWN_ACTION_CFG;
  const { Icon } = cfg;
  const label = ACTION_CONFIG[entry.action] ? t(cfg.labelKey) : entry.action;
  const rawDesc = t(cfg.describeKey, cfg.describeVars(entry, t));
  const actorName = entry.actor?.name || t("iam.auditLog.fallback.unknownActor");

  const absoluteTimestamp = useMemo(
    () => new Date(entry.timestamp).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZone: "UTC", timeZoneName: "short",
    }),
    [entry.timestamp],
  );

  return (
    <div
      ref={(el) => onSetRef(entry.id, el)}
      data-audit-id={entry.id}
      className={cn(
        !isLast && "border-b border-border/50",
        // Multi-stage spotlight keyframe defined in globals.css. Rendered as a
        // CSS animation (not a transition) so the burst → settle → decay arc
        // plays even when isHighlighted flips on instantly.
        isHighlighted && "audit-row-spotlight",
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(entry.id)}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t("iam.auditLog.detail.collapse") : t("iam.auditLog.detail.expand")}
        className={cn(
          "group/row relative flex w-full items-start gap-3.5 px-4 py-3 text-left transition-all",
          "focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40",
          "hover:bg-muted/40",
          isExpanded && "bg-muted/40",
        )}
      >
        {/* Left accent bar appears on hover/expand for a subtle anchor. */}
        <span
          aria-hidden
          className={cn(
            "absolute inset-y-0 left-0 w-[2px] origin-top scale-y-0 rounded-r bg-primary/60 transition-transform duration-200",
            (isExpanded || isHighlighted) && "scale-y-100",
            "group-hover/row:scale-y-100",
          )}
        />

        <div
          className={cn(
            "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ring-border/60 transition-shadow",
            cfg.iconBg,
            isExpanded && "ring-border shadow-sm",
          )}
        >
          <Icon className={cn("h-3.5 w-3.5", cfg.iconColor)} />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <div className="flex flex-wrap items-baseline gap-x-1 text-sm leading-snug">
            <span
              className={cn(
                "font-semibold",
                entry.actor ? "text-foreground" : "text-muted-foreground italic",
              )}
            >
              {actorName}
            </span>
            <span className="text-muted-foreground">{parseBold(rawDesc)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-1.5 font-mono text-[11px] text-muted-foreground">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default underline-offset-2 decoration-dotted hover:text-foreground hover:underline">
                  {relativeTimeAudit(entry.timestamp)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{absoluteTimestamp}</TooltipContent>
            </Tooltip>
            {entry.meta?.location && <><span aria-hidden>·</span><span>{entry.meta.location}</span></>}
            {entry.meta?.device && <><span aria-hidden>·</span><span className="max-w-[200px] truncate">{entry.meta.device}</span></>}
          </div>
        </div>

        <ChevronDown
          className={cn(
            "mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-all duration-200",
            isExpanded ? "rotate-0 opacity-100" : "-rotate-90 opacity-0 group-hover/row:opacity-100",
          )}
        />
      </button>

      {/* Expanded "event container" — animated grid expansion to avoid jump. */}
      <div
        className={cn(
          "grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out",
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0">
          <div
            className={cn(
              "group/event-panel mx-4 mb-3 overflow-hidden rounded-lg border border-border/70 bg-muted/25",
              "transition-opacity duration-200",
              isExpanded ? "opacity-100" : "opacity-0",
            )}
          >
            {/* Action ribbon — semantic color matches the action category. */}
            <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-background/40 px-3 py-1.5">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10.5px] font-semibold ring-1 ring-inset ring-current/15",
                    cfg.iconBg,
                    cfg.iconColor,
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {label}
                </span>
                <span className="font-mono text-[10.5px] text-muted-foreground/70">#{entry.id.slice(0, 8)}</span>
              </div>
              <CopyButton value={entry.id} label={t("iam.auditLog.copyEventId", "event ID")} t={t} size="xs" />
            </div>

            <div className="grid gap-x-6 gap-y-3 px-3.5 py-3 md:grid-cols-2">
              <DetailSection icon={Zap} title={t("iam.auditLog.detail.section.event")}>
                <DetailRow t={t} label={t("iam.auditLog.detail.timestamp")} value={absoluteTimestamp} />
                {(entry.org || entry.tenantId) && (
                  <DetailRow
                    t={t}
                    label={t("iam.auditLog.detail.organization")}
                    value={entry.org?.name ?? entry.tenantId!}
                    copyValue={entry.tenantId}
                    mono={!entry.org}
                  />
                )}
                {entry.target.name && <DetailRow t={t} label={t("iam.auditLog.detail.target")}      value={entry.target.name} />}
                {entry.target.email && (
                  <DetailRow t={t} label={t("iam.auditLog.detail.targetEmail")} value={entry.target.email} copyValue={entry.target.email} mono />
                )}
                {entry.meta?.to   && <DetailRow t={t} label={t("iam.auditLog.detail.grantedRole")} value={entry.meta.to} />}
                {entry.meta?.from && <DetailRow t={t} label={t("iam.auditLog.detail.revokedRole")} value={entry.meta.from} />}
              </DetailSection>

              {entry.actor && (
                <DetailSection icon={UserRound} title={t("iam.auditLog.detail.section.actor")}>
                  <DetailRow t={t} label={t("iam.auditLog.detail.name")} value={entry.actor.name} />
                  {entry.actor.email && (
                    <DetailRow t={t} label={t("iam.auditLog.detail.email")} value={entry.actor.email} copyValue={entry.actor.email} mono />
                  )}
                </DetailSection>
              )}

              {(entry.meta?.ip || entry.meta?.userAgent || entry.meta?.requestId) && (
                <DetailSection icon={Globe2} title={t("iam.auditLog.detail.section.context")}>
                  {entry.meta?.ip && (
                    <DetailRow t={t} label={t("iam.auditLog.detail.ip")} value={entry.meta.ip} copyValue={entry.meta.ip} mono />
                  )}
                  {entry.meta?.userAgent && (
                    <DetailRow
                      t={t}
                      label={t("iam.auditLog.detail.userAgent")}
                      copyValue={entry.meta.userAgent}
                      value={
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block min-w-0 truncate">
                              {entry.meta.device ?? entry.meta.userAgent}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[420px] break-all font-mono text-[10.5px]">
                            {entry.meta.userAgent}
                          </TooltipContent>
                        </Tooltip>
                      }
                    />
                  )}
                  {entry.meta?.requestId && (
                    <DetailRow t={t} label={t("iam.auditLog.detail.requestId")} value={entry.meta.requestId} copyValue={entry.meta.requestId} mono />
                  )}
                </DetailSection>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ─── Excel export ─────────────────────────────────────────────────────────────

async function exportEntriesToExcel(
  entries: AuditEntry[],
  rawItems: AuditLogItem[],
  filename: string,
  t: (key: string, defaultValue: string) => string,
): Promise<void> {
  const { utils, writeFile } = await import("xlsx");

  const rawById = new Map(rawItems.map((r) => [r.id, r]));
  const h = (key: string, defaultValue: string) => t(`iam.auditLog.export.headers.${key}`, defaultValue);
  const systemActor = t("iam.auditLog.export.systemActor", "System");

  const rows = entries.map((e) => {
    const raw = rawById.get(e.id);
    return {
      [h("timestamp", "Timestamp")]:    new Date(e.timestamp).toISOString(),
      [h("actorName", "Actor Name")]:   e.actor?.name ?? systemActor,
      [h("actorEmail", "Actor Email")]: e.actor?.email ?? "",
      [h("action", "Action")]:          e.action,
      [h("target", "Target")]:          e.target.name,
      [h("targetEmail", "Target Email")]: e.target.email ?? "",
      [h("targetType", "Target Type")]: e.target.type,
      [h("organization", "Organization")]: e.org?.name ?? "",
      [h("ip", "IP")]:                  e.meta?.ip ?? "",
      [h("device", "Device")]:          e.meta?.device ?? "",
      [h("userAgent", "User Agent")]:   e.meta?.userAgent ?? "",
      [h("requestId", "Request ID")]:   e.meta?.requestId ?? "",
      [h("event", "Event")]:            raw ? JSON.stringify(raw.metadata) : "",
    };
  });

  const ws = utils.json_to_sheet(rows);

  // Auto-width columns
  const colWidths = Object.keys(rows[0] ?? {}).map((key) => ({
    wch: Math.max(key.length, ...rows.map((r) => String(r[key as keyof typeof r] ?? "").length).slice(0, 50)) + 2,
  }));
  ws["!cols"] = colWidths;

  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, t("iam.auditLog.export.worksheetName", "Audit Log"));
  writeFile(wb, filename);
}

// ─── AuditLogModal ────────────────────────────────────────────────────────────

export interface AuditLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, the modal scrolls to and pulses the matching row on open. */
  highlightId?: string | null;
  /**
   * Pre-fills the search input on open so the modal lands already filtered
   * to a specific user/invite/role. Used by the "View in audit log" item in
   * row-level "..." menus across the dashboard. Date range is widened to
   * "all" so historical events are visible.
   */
  searchPrefill?: string | null;
  /**
   * Pre-selects an action category filter on open.
   * e.g. "access" from the roles tab, "identity" from the invites tab.
   */
  category?: ActionCategory | null;
}

export function AuditLogModal({ open, onOpenChange, highlightId, searchPrefill, category: categoryProp }: AuditLogModalProps) {
  const { t } = useTranslation();

  const [search, setSearch]           = useState("");
  const [category, setCategory]       = useState<ActionCategory>("all");
  const [actorFilter, setActorFilter] = useState("");
  const [dateRange, setDateRange]     = useState<DateRange>("7d");
  const [expandedId, setExpandedId]   = useState<string | null>(null);
  const [pulseId, setPulseId]         = useState<string | null>(null);

  // Filter state updates are marked as non-urgent transitions so React can
  // yield to the browser between render chunks. This eliminates the
  // "setTimeout handler took Xms" violations on filter chip clicks — the
  // blocking reconciliation of ~100 rows becomes interruptible.
  const [isFiltering, startFilterTransition] = useTransition();

  // Deferred body render: on open, show the dialog chrome + skeleton first
  // (fast), then enable the full list on the next animation frame. This splits
  // the 500ms synchronous mount into two smaller paints. On close and reopen,
  // a requestAnimationFrame is still used but query data is cached so rows
  // paint without a network round-trip. The ref avoids extra state on close.
  const [bodyReady, setBodyReady] = useState(false);
  const rafRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    // Only act on open. On close, do nothing — Radix plays its exit animation
    // and then unmounts the content naturally, resetting this state with it.
    // Resetting here would swap the list for a skeleton mid-animation.
    if (!open) return;
    rafRef.current = requestAnimationFrame(() => setBodyReady(true));
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, [open]);


  const rowRefs = useRef(new Map<string, HTMLDivElement>());
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Memoize so Date.now() is called only when dateRange changes, not on every render.
  // Without this, the query key changes every millisecond → infinite fetches.
  const fromDate = useMemo(() => dateRangeToFromDate(dateRange), [dateRange]);

  // Memoize actionIn so the query key is stable across unrelated renders.
  const actionIn = useMemo(
    () => category !== "all"
      ? AUDIT_CATEGORY_ACTIONS[category as keyof typeof AUDIT_CATEGORY_ACTIONS] ?? undefined
      : undefined,
    [category],
  );

  // Cursor-based infinite query — pages of 50 events stream in as the user
  // scrolls. Switching filters resets to page 1 because the query key changes.
  // `placeholderData: keepPreviousData` (set inside the queryOptions factory)
  // keeps the prior pages visible during refetch so filter chips don't flash.
  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    globalAuditLogInfiniteQueryOptions({
      actionIn,
      actorId:  actorFilter || undefined,
      fromDate,
      pageSize: 50,
    }),
  );

  // Auto-load the next page when the user nears the bottom of the scroll
  // container. Disabled until the first page has rendered so we don't fire
  // a duplicate fetch on mount.
  useInfiniteScroll(
    sentinelRef,
    scrollContainerRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    { enabled: open && bodyReady && !isLoading },
  );

  // Separate unfiltered fetch so the actors dropdown always shows all actors
  // regardless of which category or date range is currently active.
  const { data: allActorsData } = useQuery(
    globalAuditLogQueryOptions({ limit: 200 }),
  );

  // Flatten paginated items once per page-change. Memoized so `filtered` /
  // `grouped` and `AuditEntryRow` props stay reference-stable between paints.
  const rawItems: AuditLogItem[] = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const entries: AuditEntry[] = useMemo(
    () => rawItems.map(apiItemToEntry),
    [rawItems],
  );

  const actors = useMemo(() => {
    const map = new Map<string, string>();
    (allActorsData?.items ?? []).forEach((item) => {
      const key  = item.actorUserId ?? item.actorEmail;
      const name = item.actorDisplayName ?? item.actorEmail;
      if (key && name) map.set(key, name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allActorsData]);

  const allowedActions = category !== "all"
    ? AUDIT_CATEGORY_ACTIONS[category as keyof typeof AUDIT_CATEGORY_ACTIONS]
    : null;

  const filtered = useMemo(() => {
    // Split into tokens so "Admins Nexus" matches entries that contain BOTH
    // "admins" AND "nexus" anywhere in the event fields (AND semantics).
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return entries.filter((e) => {
      if (!inRange(e.timestamp, dateRange)) return false;
      if (allowedActions && !(allowedActions as string[]).includes(e.action)) return false;
      if (actorFilter && e.actor?.id !== actorFilter && e.actor?.email !== actorFilter) return false;
      if (tokens.length > 0) {
        // Name/org fields: always searched.
        const coreHay = [
          e.actor?.name ?? "",
          e.target.name,
          e.org?.name   ?? "",
          e.meta?.to    ?? "",
          e.meta?.from  ?? "",
        ].join(" ").toLowerCase();

        // Email fields are only matched when the token looks like an email
        // (contains "@"). This prevents email domains (e.g. "adge" in
        // "alex@adge.ai") from falsely matching an org-name or role-name token.
        if (!tokens.every((tok) => {
          if (coreHay.includes(tok)) return true;
          if (tok.includes("@")) {
            const actorEmail  = (e.actor?.email  ?? "").toLowerCase();
            const targetEmail = (e.target.email  ?? "").toLowerCase();
            if (actorEmail.includes(tok) || targetEmail.includes(tok)) return true;
          }
          return false;
        })) return false;
      }
      return true;
    });
  }, [entries, search, dateRange, allowedActions, actorFilter]);

  const grouped = useMemo(() => {
    const groups = new Map<string, AuditEntry[]>();
    for (const entry of filtered) {
      const key = dateKey(entry.timestamp, t);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(entry);
    }
    return Array.from(groups.entries());
  }, [filtered, t]);

  const hasFilters = !!search || category !== "all" || !!actorFilter || dateRange !== "7d";

  const clearAll = useCallback(() => {
    startFilterTransition(() => {
      setSearch(""); setCategory("all"); setActorFilter(""); setDateRange("7d");
    });
  }, [startFilterTransition]);

  // Delay cleanup until after the Dialog exit animation (~200ms) so the
  // expanded detail panel doesn't visibly collapse before the modal fades out.
  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => {
      clearAll();
      setExpandedId(null);
      setPulseId(null);
    }, 250);
    return () => clearTimeout(timer);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenChange = useCallback((v: boolean) => {
    onOpenChange(v);
  }, [onOpenChange]);

  // Stable identity so memo'd AuditEntryRow skips rows whose expand state
  // hasn't changed. Without this every click re-renders all ~100 rows.
  const toggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  }, []);

  // Stable ref registration callback — never recreated, never breaks memo.
  const setRowRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else    rowRefs.current.delete(id);
  }, []);

  // When opening with a highlightId or searchPrefill, relax filters so the
  // intended row(s) are guaranteed visible. Re-runs on every transition so
  // clicks from drawer "..." menus always land on the right view.
  useEffect(() => {
    if (!open) return;
    if (highlightId) {
      setSearch("");
      setCategory("all");
      setActorFilter("");
      setDateRange("all");
      setExpandedId(highlightId);
      return;
    }
    if (searchPrefill) {
      setSearch(searchPrefill);
      setCategory(categoryProp ?? "all");
      setActorFilter("");
      setDateRange("all");
      return;
    }
    if (categoryProp) {
      setSearch("");
      setCategory(categoryProp);
      setActorFilter("");
      setDateRange("all");
    }
  }, [open, highlightId, searchPrefill, categoryProp]);

  // When opening to a specific row that hasn't loaded yet, keep paging through
  // history until either the row appears or the cursor is exhausted. Capped at
  // 4 extra pages (~200 events on top of the first 50) so a stale highlight
  // can't trigger an unbounded crawl.
  const highlightPageFetchesRef = useRef(0);
  useEffect(() => {
    if (!open || !highlightId) return;
    highlightPageFetchesRef.current = 0;
  }, [open, highlightId]);
  useEffect(() => {
    if (!open || !highlightId || isLoading || isFetchingNextPage) return;
    const found = rawItems.some((item) => item.id === highlightId);
    if (found) return;
    if (!hasNextPage) return;
    if (highlightPageFetchesRef.current >= 4) return;
    highlightPageFetchesRef.current += 1;
    void fetchNextPage();
  }, [open, highlightId, isLoading, isFetchingNextPage, hasNextPage, rawItems, fetchNextPage]);

  // Once data has loaded and the row is in the DOM, scroll to it and trigger the pulse.
  // We watch `entries` instead of `data` so layout-dependent measurements use post-render state.
  //
  // Two-stage timing for a polished arrival:
  //  1. ~280ms — let the smooth scroll settle so the row isn't moving when the
  //     animation starts (otherwise the spotlight visually "drags" with the row).
  //  2. ~2000ms — matches the spotlight keyframe duration in globals.css and
  //     leaves a small buffer before the class is removed.
  useEffect(() => {
    if (!open || !highlightId || isLoading) return;
    const el = rowRefs.current.get(highlightId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });

    const startTimer = setTimeout(() => setPulseId(highlightId), 280);
    const endTimer   = setTimeout(() => setPulseId(null),       280 + 1500);
    return () => {
      clearTimeout(startTimer);
      clearTimeout(endTimer);
    };
  }, [open, highlightId, isLoading, entries]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-full max-w-[1400px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1400px]">

        {/* ── Header ──────────────────────────────────── */}
        <DialogIconHeader
          icon={Clock}
          title={t("iam.auditLog.title")}
          description={t("iam.auditLog.description")}
          className="pr-14"
        />

        {/* ── Filters ─────────────────────────────────── */}
        <div className="flex flex-col gap-2.5 border-b border-border bg-muted/20 px-6 py-3">
          <div className="flex items-center gap-2">
            <label className="relative flex h-8 flex-1 items-center gap-2 rounded-md border border-border bg-background px-2.5 focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40">
              <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <input
                type="text"
                role="searchbox"
                placeholder={t("iam.auditLog.filters.searchPlaceholder")}
                value={search}
                onChange={(e) => {
                  const v = e.target.value;
                  startFilterTransition(() => setSearch(v));
                }}
                className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
              />
              {search && (
                <button type="button" onClick={() => startFilterTransition(() => setSearch(""))} className="shrink-0 rounded text-muted-foreground hover:text-foreground">
                  <X className="h-3 w-3" />
                </button>
              )}
            </label>
            <select
              value={actorFilter}
              onChange={(e) => {
                const v = e.target.value;
                startFilterTransition(() => setActorFilter(v));
              }}
              className="h-8 min-w-[128px] rounded-md border border-border bg-background px-2.5 text-xs text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/40"
            >
              <option value="">{t("iam.auditLog.filters.allActors")}</option>
              {actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1">
              {ACTION_CATEGORIES.map((cat) => (
                <FilterChip key={cat} active={category === cat} onClick={() => startFilterTransition(() => setCategory(cat))}>
                  {t(`iam.auditLog.filters.categories.${cat}`)}
                </FilterChip>
              ))}
            </div>
            <div className="h-4 w-px bg-border" />
            <div className="flex items-center gap-1">
              {DATE_RANGES.map((dr) => (
                <FilterChip key={dr} active={dateRange === dr} onClick={() => startFilterTransition(() => setDateRange(dr))}>
                  {t(`iam.auditLog.filters.dateRanges.${dr}`)}
                </FilterChip>
              ))}
            </div>
            {hasFilters && (
              <>
                <div className="h-4 w-px bg-border" />
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  {t("iam.auditLog.clearAll")}
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Body ────────────────────────────────────── */}
        <div
          ref={scrollContainerRef}
          className={cn("flex-1 overflow-y-scroll px-6 py-4 transition-opacity duration-150", isFiltering && "opacity-60 pointer-events-none")}
        >
          {!bodyReady ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : isLoading ? (
            <div className="flex flex-col gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
                <X className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <p className="text-sm font-medium">{t("iam.auditLog.errorTitle")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("iam.auditLog.errorHint")}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => void refetch()}>
                {t("iam.auditLog.retry")}
              </Button>
            </div>
          ) : grouped.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-20 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Clock className="h-6 w-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">{t("iam.auditLog.noEvents")}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t("iam.auditLog.noEventsHint")}</p>
              </div>
              {hasFilters && (
                <Button variant="outline" size="sm" onClick={clearAll}>{t("iam.auditLog.clearFilters")}</Button>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {grouped.map(([label, groupEntries]) => (
                <div key={label}>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
                    <div className="h-px flex-1 bg-border" />
                    <span className="tabular-nums text-[10.5px] text-muted-foreground">{groupEntries.length}</span>
                  </div>

                  <div className="overflow-hidden rounded-lg border border-border">
                    {groupEntries.map((entry, i) => (
                      <AuditEntryRow
                        key={entry.id}
                        entry={entry}
                        isLast={i === groupEntries.length - 1}
                        isExpanded={expandedId === entry.id}
                        isHighlighted={pulseId === entry.id}
                        onToggle={toggleExpand}
                        onSetRef={setRowRef}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              ))}

              {/* Sentinel + status row sits at the bottom of the list. The
                  hook reads the scroll container's distance-to-bottom rather
                  than this element's IntersectionObserver, but we keep the
                  ref + status here so manual "Load more" clicks have a target
                  and the user always sees fetch progress. */}
              <div
                ref={sentinelRef}
                className="flex items-center justify-center py-3 text-[11px] text-muted-foreground"
              >
                {isFetchingNextPage ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t("iam.auditLog.loadingMore", { defaultValue: "Loading more events…" })}
                  </span>
                ) : hasNextPage ? (
                  <button
                    type="button"
                    onClick={() => void fetchNextPage()}
                    className="font-medium text-foreground underline-offset-2 hover:underline"
                  >
                    {t("iam.auditLog.loadMore", { defaultValue: "Load more" })}
                  </button>
                ) : entries.length > 0 ? (
                  <span>{t("iam.auditLog.endOfResults", { defaultValue: "End of results" })}</span>
                ) : null}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────── */}
        <div className="flex items-center justify-between border-t border-border px-6 py-3">
          <span className="text-xs text-muted-foreground">
            {isLoading
              ? t("iam.auditLog.loading")
              : t("iam.auditLog.showing", { filtered: filtered.length, total: rawItems.length })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={filtered.length === 0}
              onClick={() => void exportEntriesToExcel(filtered, rawItems, `audit-log-${new Date().toISOString().slice(0, 10)}.xlsx`, t)}
            >
              <Download className="h-3.5 w-3.5" />
              {t("iam.auditLog.exportCsv")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
              {t("iam.auditLog.close")}
            </Button>
          </div>
        </div>

      </DialogContent>
    </Dialog>
  );
}
